import { TmBaseComponent } from '@treasury/presentation';
import { attachIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-button';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Attachment, Message } from '../types/message.types';
import './compose-message-form';

export const tagName = 'message-detail';
@customElement(tagName)
export class MessageDetail extends TmBaseComponent {
    @property({ type: Array })
    public messages: Message[] = [];

    @property({ type: Boolean })
    public loading = false;

    @property({ type: Boolean })
    public downloading = false;

    @state()
    private isReplying = false;

    protected render() {
        return html`<tm-body
            ><div class="button-bar">${this.renderActionButtons()}</div>
            ${this.renderMessageForm()} ${this.renderMessageDetails()}</tm-body
        >`;
    }

    private archiveMessage() {
        this.dispatchEvent(new CustomEvent('archive-message'));
    }

    private replyToMessage() {
        this.isReplying = true;
    }

    private renderMessageForm() {
        if (!this.isReplying) {
            return nothing;
        }
        return html`<compose-message-form
            .message=${this.messages}
            .isReplying=${this.isReplying}
            @send-message=${(e: CustomEvent) => {
                this.dispatchEvent(new CustomEvent('send-message', { detail: e.detail }));
                this.isReplying = false;
            }}
            @cancel-message=${() => {
                this.isReplying = false;
                this.dispatchEvent(new CustomEvent('cancel-message'));
            }}
            @cancel=${() => {
                this.isReplying = false;
            }}
        ></compose-message-form>`;
    }

    private renderMessageDetails() {
        if (this.messages.length) {
            const messages = this.messages.map(
                (message: Message) =>
                    html`<div class="message-details text-sm">
                        <div class="message-details-header mb-2">
                            <div class="message-details-header-left">
                                <div class="message-id font-medium">${message.messageId}</div>
                                <div class="text-xs">
                                    From
                                    <span class="from-user-text font-medium"
                                        >${message.fromToUserName}</span
                                    >
                                </div>
                            </div>
                            <div class="message-time text-xs text-right">
                                <div>${message.sentDate}</div>
                                <div>${message.sentTime}</div>
                            </div>
                        </div>
                        <div class="message-details-body">
                            <p>${message.body}</p>
                        </div>
                        ${this.renderAttachmentDownload(message)}
                    </div>`
            );
            return html`<div class="messages-wrapper px-1 text-header">${messages}</div>`;
        }
        return nothing;
    }

    private renderAttachmentDownload(message: Message) {
        if (message.attachments.length < 1) {
            return nothing;
        }

        return message.attachments.map(
            (attachment: Attachment) => html`
                <tm-button
                    class="mt-2"
                    importance="tertiary"
                    @click=${() =>
                        this.dispatchEvent(
                            new CustomEvent('download-attachment', {
                                detail: {
                                    attachmentId: message.attachments[0].id,
                                    attachmentName: message.attachments[0].name,
                                },
                            })
                        )}
                    ><span>${attachIcon} ${attachment.name}</span>
                </tm-button>
            `
        );
    }

    private renderActionButtons() {
        return html`
            <tm-button
                class="m-1"
                .disabled=${this.messages[0]?.isArchived}
                @click=${() => this.archiveMessage()}
            >
                Archive
            </tm-button>
            <tm-button class="m-1" @click=${() => this.replyToMessage()}> Reply </tm-button>
        `;
    }

    public static get styles() {
        return [
            css`
                .button-bar {
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    width: 100%;
                }
                .message-details:not(:last-child) {
                    padding-bottom: 10px;
                    border-bottom: 1px dashed var(--border-color);
                }
                .message-details:not(:first-child) {
                    padding-top: 10px;
                    border-top: 1px dashed var(--border-color);
                }
                .message-details-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                }
                .from-text {
                    color: var(--secondary-text-color);
                    font-size: 14px;
                    line-height: 16px;
                }
                .from-user-text {
                    color: var(--header-text-color);
                    font-size: 14px;
                    line-height: 16px;
                    font-weight: 500;
                }
                svg {
                    display: inline-block;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: MessageDetail;
    }
}
